import { Button, Input, Pagination, Space, Spin, Table } from 'antd'
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import Card from 'components/Card'
import DeviceActionDrawer from './components/DeviceActionDrawer'
import RegisterDeviceDrawer from './components/RegisterDeviceDrawer'

import '../../global/styles/global.less'
import './deviceManagement.less'
import Heading from 'components/Heading'

const { Search } = Input

const DEFAULT_PAGE_SIZE = 15

const DeviceManagement = ({
  devices,
  registerDeviceError,
  registeringDevice,
  registerDevice,
  requestDevices,
  requestingDevices,
  searchQuery,
  setDeviceSearchQuery,
  setRegisterDeviceError,
  shipOptions,
  requestingShipOptions,
}) => {
  const { t } = useTranslation()

  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery)
  const [currentPage, setCurrentPage] = useState(1)
  const [isRegisterDrawerVisible, setIsRegisterDrawerVisible] = useState(false)
  const [actionDrawer, setActionDrawer] = useState({
    mode: 'view',
    record: null,
    visible: false,
  })

  const openActionDrawer = (record, mode) => {
    setActionDrawer({ mode, record, visible: true })
  }

  const closeActionDrawer = () => {
    setActionDrawer(prev => ({ ...prev, visible: false }))
  }

  const handleSaveSuccess = () => {
    requestDevices()
  }

  useEffect(() => {
    setLocalSearchQuery(searchQuery)
    setCurrentPage(1)
  }, [searchQuery])

  const paginatedDevices = devices.slice(
    (currentPage - 1) * DEFAULT_PAGE_SIZE,
    currentPage * DEFAULT_PAGE_SIZE
  )

  const columns = [
    {
      dataIndex: 'vesselName',
      key: 'vesselName',
      sorter: (a, b) => (a.vesselName ?? '').localeCompare(b.vesselName ?? ''),
      title: t('deviceManagement.table.vesselName'),
    },
    {
      dataIndex: 'deviceFriendlyName',
      key: 'deviceFriendlyName',
      sorter: (a, b) =>
        (a.deviceFriendlyName ?? '').localeCompare(b.deviceFriendlyName ?? ''),
      title: t('deviceManagement.table.deviceFriendlyName'),
    },
    {
      dataIndex: 'status',
      key: 'status',
      render: value => {
        const label = value
          ? t('deviceManagement.registerDrawer.statusActive')
          : t('deviceManagement.registerDrawer.statusInactive')

        return (
          <span
            className={
              value
                ? 'device-management__status--active'
                : 'device-management__status--inactive'
            }
          >
            {label}
          </span>
        )
      },
      sorter: (a, b) => Number(b.status) - Number(a.status),
      title: t('deviceManagement.table.status'),
    },
    {
      dataIndex: 'enrolledDate',
      key: 'enrolledDate',
      sorter: (a, b) =>
        (a.enrolledDate ?? '').localeCompare(b.enrolledDate ?? ''),
      title: t('deviceManagement.table.enrollmentDate'),
    },
    {
      dataIndex: 'deviceAge',
      key: 'deviceAge',
      sorter: (a, b) => a.deviceAgeMonths - b.deviceAgeMonths,
      title: t('deviceManagement.table.deviceAge'),
    },
    {
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button
            onClick={() => openActionDrawer(record, 'view')}
            size="small"
            type="link"
          >
            {t('deviceManagement.table.view')}
          </Button>
          <Button
            onClick={() => openActionDrawer(record, 'edit')}
            size="small"
            type="link"
          >
            {t('deviceManagement.table.edit')}
          </Button>
        </Space>
      ),
      title: t('deviceManagement.table.actions'),
    },
  ]

  const handleSearchChange = e => {
    const value = e.target.value
    setLocalSearchQuery(value)
    setDeviceSearchQuery(value)
  }

  const handleSearchClear = value => {
    // Called by onSearch when the clear button is used (value will be '')
    setLocalSearchQuery(value)
    setDeviceSearchQuery(value)
  }

  const handleRegisterDevice = ({ onSuccess, payload }) => {
    registerDevice({ onSuccess, payload })
  }

  return (
    <div className="page__container">
      <Heading level={1}>{t('deviceManagement.title')}</Heading>
      <Card>
        <div className="device-management__search">
          <Search
            allowClear
            enterButton
            onChange={handleSearchChange}
            onSearch={handleSearchClear}
            placeholder={t('deviceManagement.searchPlaceholder')}
            value={localSearchQuery}
          />
          <Button
            onClick={() => setIsRegisterDrawerVisible(true)}
            type="primary"
          >
            {t('deviceManagement.registerDevice')}
          </Button>
        </div>

        <Spin spinning={requestingDevices}>
          <Table
            columns={columns}
            dataSource={paginatedDevices}
            pagination={false}
            rowKey="id"
            size="middle"
          />
        </Spin>

        <div className="device-management__footer">
          <Pagination
            className="device-management__pagination"
            current={currentPage}
            onChange={setCurrentPage}
            pageSize={DEFAULT_PAGE_SIZE}
            showSizeChanger={false}
            showTitle={false}
            total={devices.length}
          />
        </div>
      </Card>

      <RegisterDeviceDrawer
        error={registerDeviceError}
        isSaving={registeringDevice}
        onClose={() => setIsRegisterDrawerVisible(false)}
        onRegister={handleRegisterDevice}
        requestingShipOptions={requestingShipOptions}
        setRegisterDeviceError={setRegisterDeviceError}
        shipOptions={shipOptions}
        visible={isRegisterDrawerVisible}
      />

      <DeviceActionDrawer
        mode={actionDrawer.mode}
        onClose={closeActionDrawer}
        onSaveSuccess={handleSaveSuccess}
        record={actionDrawer.record}
        requestingShipOptions={requestingShipOptions}
        shipOptions={shipOptions}
        visible={actionDrawer.visible}
      />
    </div>
  )
}

DeviceManagement.defaultProps = {
  devices: [],
  registerDeviceError: null,
  searchQuery: '',
}

DeviceManagement.propTypes = {
  devices: PropTypes.array,
  registerDevice: PropTypes.func.isRequired,
  registerDeviceError: PropTypes.string,
  registeringDevice: PropTypes.bool.isRequired,
  requestDevices: PropTypes.func.isRequired,
  requestingDevices: PropTypes.bool.isRequired,
  requestingShipOptions: PropTypes.bool,
  searchQuery: PropTypes.string,
  setDeviceSearchQuery: PropTypes.func.isRequired,
  setRegisterDeviceError: PropTypes.func.isRequired,
  shipOptions: PropTypes.array,
}

export default DeviceManagement
